import { z } from "zod";
import {
  whiteboardElementSchema,
  whiteboardFileSchema,
  type WhiteboardScene,
} from "@/lib/whiteboard/schema";

// What a board's live channel carries: who has it open, where their pointer
// is, and the shapes they change as they draw. Shared by the server hub
// (live.server.ts) and the browser hook; no server code lives here.

/** Request header naming the tab that saved, so its own save is not announced back to it. */
export const WHITEBOARD_CLIENT_HEADER = "x-whiteboard-client";

export const whiteboardClientIdSchema = z.string().min(8).max(64);

export interface WhiteboardLivePerson {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

/** One open tab of the board. */
export interface WhiteboardLiveClient {
  clientId: string;
  user: WhiteboardLivePerson;
}

export type WhiteboardLiveElement = WhiteboardScene["elements"][number];

export type WhiteboardLiveFile = WhiteboardScene["files"][number];

export type WhiteboardLiveEvent =
  /**
   * First thing on a new stream: the key this tab sends its pointer and shapes
   * with, the board's saved revision, and what was drawn live since the room
   * opened (saved or not), so a newcomer starts from what the others see.
   */
  | {
      type: "ready";
      token: string;
      revision: number;
      canEdit: boolean;
      elements: WhiteboardLiveElement[];
      files: WhiteboardLiveFile[];
    }
  /** Who is here; sent whole, on every arrival and departure. */
  | { type: "state"; clients: WhiteboardLiveClient[] }
  /** A tab's pointer (scene coordinates, null once it leaves the board) and selection. */
  | {
      type: "pointer";
      clientId: string;
      pointer: { x: number; y: number } | null;
      pressed: boolean;
      selectedIds: string[];
    }
  /** Shapes another tab changed, deletions included (`isDeleted`). */
  | {
      type: "elements";
      clientId: string;
      elements: WhiteboardLiveElement[];
      files: WhiteboardLiveFile[];
    }
  /**
   * The board was saved. `live`: the drawing in it already came over this
   * channel; otherwise it came from a tab that is not connected, so read it.
   */
  | {
      type: "saved";
      clientId: string | null;
      revision: number;
      scene: boolean;
      library: boolean;
      /** Title, chantier or external link: the header reads the board again. */
      details: boolean;
      live: boolean;
    }
  /** Something the header shows changed without a new revision (the external link). */
  | { type: "details" }
  /** This person's access changed (shared differently, or revoked): read the board again. */
  | { type: "access"; canEdit: boolean }
  /** The board is gone, or no longer yours to see; the stream ends. */
  | { type: "closed" };

/** One message from a tab: any of its pointer, selection and changed shapes. */
export const whiteboardLiveMessageSchema = z.object({
  client: whiteboardClientIdSchema,
  token: z.string().min(16).max(128),
  pointer: z.object({ x: z.number().finite(), y: z.number().finite() }).nullable().optional(),
  pressed: z.boolean().optional(),
  selectedIds: z.array(z.string().max(200)).max(1_000).optional(),
  elements: z.array(whiteboardElementSchema).max(10_000).optional(),
  files: z.array(whiteboardFileSchema).max(100).optional(),
});

export type WhiteboardLiveMessage = z.infer<typeof whiteboardLiveMessageSchema>;
